import { type EventContentArg } from "@fullcalendar/core"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  calendarEventCardAddress,
  calendarEventCardRecordParts,
  calendarEventCardTitle
} from "../../helpers/calendar-event-card-title.ts"

const { Text } = Typography

interface CalendarEventCellEventProps {
  eventInfo: EventContentArg
}

const TypeDot = ({ color }: { color?: string }) => (
  <span
    className="inline-block shrink-0 size-1.5 rounded-full"
    style={{ backgroundColor: color }}
  />
)

export function CalendarEventCellEvent({
  eventInfo
}: CalendarEventCellEventProps) {
  const { t } = useTranslation()
  const event = eventInfo?.event
  const input = { title: event?.title, record: event?.extendedProps }
  const title = calendarEventCardTitle(input, t)
  const { recordName, personName } = calendarEventCardRecordParts(input, t)
  // Person and location share the card's fourth line — whichever the event
  // actually has, never both — so the card never grows past four lines
  // regardless of duration (a fifth line was overflowing the event's own
  // box in a real browser; jsdom can't see that, so it wasn't caught until
  // manual verification).
  const personOrAddress = personName || calendarEventCardAddress(input)

  // The card grows a line at a time as the slot has room for it: a
  // sub-30-minute slot is too short to hold a title line AND a separate
  // time line without one clipping into the other (confirmed live — a
  // 30-minute "Project Meeting" rendered its time line overlapping the
  // title), so under 30 minutes the time rides on the SAME line as the
  // title instead of disappearing — losing the time entirely regressed
  // every short appointment (a default 15-minute Follow-Up included) to
  // "no visible time at all". 30 minutes or longer gives the time its own
  // line and adds the lead/project; 45 or longer adds who it's with / where
  // it is.
  const diffInMinutes = dayjs(event?.end).diff(dayjs(event?.start), "minute")
  // showTime and showRecordName share the same 30-minute cutoff by
  // coincidence, not by design — they gate two independent lines and are
  // deliberately two separate constants so tuning one doesn't silently move
  // the other.
  const showTime = diffInMinutes >= 30
  const showRecordName = diffInMinutes >= 30
  const showPersonOrAddress = diffInMinutes >= 45

  // Struck through rather than hidden: whoever was invited needs to see that
  // the meeting was cancelled, not find it silently gone from the grid.
  const isCancelled = event?.extendedProps?.status === "CANCELLED"

  // Month cells are a fraction of a week/day slot's height, so they get one
  // line — type + time — rather than the full stacked identity.
  const isMonthView = eventInfo?.view?.type === "dayGridMonth"
  // FullCalendar's list view already renders the time and a colored dot in
  // its own cells (`.fc-list-event-time` / `-graphic`) — eventContent here
  // only fills the title cell, so this is title + a compact Lead/Project ·
  // participants · status line, not a rebuild of the whole row.
  const isListView = !!eventInfo?.view?.type?.startsWith("list")

  if (isListView) {
    const participantNames = event?.extendedProps?.participants
      ?.map((p: { full_name?: string }) => p?.full_name)
      .filter(Boolean)

    return (
      <Flex
        vertical
        gap={2}
        className={isCancelled ? "line-through opacity-60" : ""}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <Flex align="center" gap={6}>
          <Text strong className="text-sm">
            {title}
          </Text>
          {isCancelled && (
            <Text type="danger" className="text-[11px] font-medium">
              Cancelled
            </Text>
          )}
        </Flex>
        {!!(recordName || personOrAddress || participantNames?.length) && (
          <Text type="secondary" className="text-xs">
            {[recordName, personOrAddress, participantNames?.join(", ")]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        )}
      </Flex>
    )
  }

  if (isMonthView) {
    return (
      <Flex
        align="center"
        gap={4}
        className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <TypeDot color={event?.borderColor} />
        {eventInfo?.timeText && (
          <Text className="text-xs text-inherit opacity-80 leading-4 shrink-0">
            {eventInfo.timeText}
          </Text>
        )}
        <Text ellipsis className="text-xs text-inherit font-medium leading-4">
          {title}
        </Text>
      </Flex>
    )
  }

  // Title first, time second: the title is what a reader scans for, and the
  // event's own duration/color already anchor "when" spatially on the grid.
  // Time previously rendered ABOVE the title, which put the taller, bolder
  // title line in the space the shorter box had already spent on the time
  // line above it — confirmed live as the exact cause of the two lines
  // visually colliding on a 30-minute slot. `leading-none` (rather than the
  // fixed `leading-3`/`leading-4`) keeps each line's box as close to its
  // font size as the slot's own line-height allows.
  return (
    <Flex
      vertical
      className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
      data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
    >
      <Flex align="center" gap={4} className="min-w-0">
        <TypeDot color={event?.borderColor} />
        <Text
          ellipsis
          className="text-[12px] text-inherit font-semibold leading-none min-w-0 flex-1"
        >
          {title}
        </Text>
        {eventInfo?.timeText && !showTime && (
          <Text
            ellipsis
            className="text-[10px] text-inherit opacity-75 leading-none shrink-0"
          >
            {eventInfo.timeText}
          </Text>
        )}
      </Flex>

      {eventInfo?.timeText && showTime && (
        <Text
          ellipsis
          className="text-[10px] text-inherit opacity-75 leading-none"
        >
          {eventInfo.timeText}
        </Text>
      )}

      {recordName && showRecordName && (
        <Text ellipsis className="text-[11px] text-inherit opacity-90 leading-none">
          {recordName}
        </Text>
      )}

      {personOrAddress && showPersonOrAddress && (
        <Text ellipsis className="text-[11px] text-inherit opacity-70 leading-none">
          {personOrAddress}
        </Text>
      )}
    </Flex>
  )
}
